import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { theme } from '@config/theme';
import { useModalDimensions } from '@hooks/useModalDimensions';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import {
	AccordionProps,
	accordionSummaryClasses,
	AccordionSummaryProps,
	Box,
	CircularProgress,
	styled,
} from '@mui/material';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuidelines } from 'services/monitoring/monitoringHooks';
import { MonitoringModalProps } from 'types/monitoring';

import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';

const Accordion = styled((props: AccordionProps) => (
	<MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
	border: 'none',
	'&::before': {
		display: 'none',
	},
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
	<MuiAccordionSummary
		expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
		{...props}
	/>
))(() => ({
	padding: '10px 20px',
	borderRadius: '8px',
	height: '42px',
	minHeight: '42px',
	border: `1px solid ${theme.palette.custom.offWhite}`,
	backgroundColor: 'transparent',
	flexDirection: 'row',
	justifyContent: 'space-between',
	'& .MuiAccordion-heading': {
		borderBottom: '1px solid red !important',
		paddingBottom: '20px',
	},
	[`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
		transform: 'rotate(90deg)',
	},
	[`& .${accordionSummaryClasses.expandIconWrapper}`]: {
		margin: 0,
	},
	[`& .${accordionSummaryClasses.content}`]: {
		margin: 0,
	},
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
	marginTop: theme.spacing(),
	border: 'none',
}));

const LoadingIndicator = () => (
	<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
		<CircularProgress />
	</Box>
);

export function ExpressModal({ open, onClose, description }: MonitoringModalProps) {
	const modalDimensions = useModalDimensions();
	const { t } = useTranslation();
	const { data: guideLines, isLoading } = useGuidelines('express');

	const [expanded, setExpanded] = useState<string | false>('');

	const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
		setExpanded(newExpanded ? panel : false);
	};

	return (
		<GeneralMuiModal
			maxWidth={'xl'}
			title={t('express')}
			styles={{
				paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
				contentStyle: { padding: 0 },
			}}
			open={open}
			onClose={onClose}
			content={
				<>
					<div className="flex flex-col gap-4 p-[20px]">
						{isLoading ? (
							<LoadingIndicator />
						) : (
							<>
								<p className="text-black font-roboto text-base font-normal leading-[140%] mb-10">
									{description}
								</p>
								{guideLines?.map((guideLine: any, guideLineIndex: any) => (
									<div key={guideLineIndex} className="flex flex-col gap-4">
										<h3 className="text-black font-roboto text-base font-semibold leading-[140%] uppercase">
											{guideLine.title}
										</h3>
										{guideLine.content.map((content: any, contentIndex: any) => (
											<Accordion
												sx={{
													backgroundColor: 'soilAnalysis',
													margin: 0,
												}}
												key={contentIndex}
												expanded={expanded === content.subCategory}
												onChange={handleChange(content.subCategory)}
											>
												<AccordionSummary>
													<p
														className={classNames(
															'text-dark font-roboto text-base font-bold leading-[140%] mr-[8px]',
															{
																'text-primary': expanded === content.subCategory,
															}
														)}
													>
														{content.subCategory}
													</p>
												</AccordionSummary>

												<AccordionDetails
													className=""
													sx={{
														padding: 0,
														paddingLeft: '24px',
														margin: 0,
														marginTop: '10px',
													}}
												>
													<p className="text-black font-roboto text-base font-normal leading-[140%]">
														{content.description}
													</p>
												</AccordionDetails>
											</Accordion>
										))}
									</div>
								))}
							</>
						)}
					</div>
				</>
			}
		/>
	);
}
